/*
  Intercepts HTTP requests and returns mock data for any URL that matches a registered
  LocalRecordSet id. Register datasets via the MOCK_DATA_TOKEN multi-provider:

    providers: [
      { provide: HTTP_INTERCEPTORS, useClass: MockDataInterceptor, multi: true },
      { provide: MOCK_DATA_TOKEN, useValue: MY_MOCK_DATA, multi: true },
    ]
*/
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { inject, Injectable, InjectionToken } from "@angular/core";
import { delay, Observable, of } from "rxjs";

// EZ
import { EzBaseClass } from "@template-ui/ez-base-class";
import { getODataMockPagingResult, isLikelyODataRequest } from "./mock-odata-paging";
import { getSimpleMockPagingResult } from "./mock-simplified-paging";

/**
 * Shape of a single mock endpoint dataset registered with {@link MockDataInterceptor}.
 */
export type LocalRecordSet = {
  id: string;
  data: unknown[] | unknown;
  succeeded: boolean;
  errors: unknown[];
  message: string;
  delayInMsec?: number;
  serverSidePaging?: boolean;
};


/**
 * Multi-injection token for registering {@link LocalRecordSet} arrays with
 * {@link MockDataInterceptor}. Each provider contributes one \`LocalRecordSet[]\`;
 * all contributions are flattened into a single in-memory dataset.
 *
 * Register at the root level (alongside \`HTTP_INTERCEPTORS\`) so datasets are
 * visible to the root-scoped interceptor.
 *
 * @example
 * ```ts
 * { provide: MOCK_DATA_TOKEN, useValue: MY_FEATURE_MOCK_DATA, multi: true }
 * ```
 */
export const MOCK_DATA_TOKEN = new InjectionToken<LocalRecordSet[]>('EZ_MOCK_DATA');

/**
 * HTTP interceptor that short-circuits requests to registered mock endpoints,
 * returning in-memory data instead of hitting the real server.
 *
 * ## Registration via token (preferred for shared/feature datasets)
 * ```ts
 * { provide: MOCK_DATA_TOKEN, useValue: MY_MOCK_DATA, multi: true }
 * ```
 *
 * ## Registration via inject (preferred for per-component, easy-to-remove mocks)
 * ```ts
 * // In a component constructor — delete this line when the real endpoint is ready.
 * inject(MockDataInterceptor).addEndpoint('mock/myData', myDataArray);
 * ```
 * For `inject()` to work, `MockDataInterceptor` must be provided as itself
 * (use `useExisting` on the `HTTP_INTERCEPTORS` token — see `ez-lib.provider.ts`).
 */
@Injectable()
export class MockDataInterceptor extends EzBaseClass implements HttpInterceptor {
  // ───────────────────────────────────────────────────────────────
  //  Private/Internal State
  // ───────────────────────────────────────────────────────────────

  /** Flattened list of all registered mock datasets. */
  private allMockData: LocalRecordSet[];

  constructor() {
    super();
    this.thisClassName = 'MockDataInterceptor';
    this.dlogEnabled = true;
    const groups = inject(MOCK_DATA_TOKEN, { optional: true }) as LocalRecordSet[][] | null;
    this.allMockData = groups?.flat() ?? [];
  }

  /**
   * Registers a mock endpoint at runtime. Useful for component-level mocks that
   * should live next to the component and be deleted when the real endpoint is ready.
   *
   * If an entry with the same `id` already exists it is replaced.
   *
   * @example
   * ```ts
   * // In a component constructor — remove when the real endpoint exists.
   * inject(MockDataInterceptor).addEndpoint('mock/myData', myDataArray);
   * ```
   */
  public addEndpoint(id: string, data: unknown, delayInMsec?: number): void {
    const record: LocalRecordSet = { id, data, succeeded: true, errors: [], message: '', delayInMsec };
    const existing = this.allMockData.findIndex((d) => d.id.toLowerCase() === id.toLowerCase());
    if (existing >= 0) {
      this.allMockData[existing] = record;
    } else {
      this.allMockData.push(record);
    }
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    // See if there's a mock data entry for the API that was called
    const record = this.allMockData.find((d) => {
      return req.url.toLowerCase().includes(d.id.toLowerCase());
    });

    // If the entry was found, return from allMockData and don't make the API call.
    if (record) {
      this.dlog(`Intercepting ${req.method} ${req.url} ` + (req.method !== 'GET' ? `body: ${JSON.safeStringify(req.body)}` : ''));

      if (req.method !== 'GET') {
        const updated = this.applyMutation(record, req);
        return of(new HttpResponse({
          status: 200,
          body: {
            data: updated,
            succeeded: true,
            message: 'Mock record updated'
          }
        })).pipe(delay(record.delayInMsec ?? 250));
      }

      // Non-array data (object/scalar): return as-is — no paging wrapper, matches
      // real API responses that return a plain object (e.g. { documentTypeList: [...] }).
      if (!Array.isArray(record.data)) {
        return of(new HttpResponse({
          status: 200,
          body: record.data
        })).pipe(delay(record.delayInMsec ?? 500));
      }

      // Decide parser and produce paged response
      const url = new URL(req.url, window.location.origin);
      const pagingResult = this.isODataRequest(url)
        ? getODataMockPagingResult(url, record.data)
        : getSimpleMockPagingResult(url, record.data);

      const responseBody = {
        ...record,
        ...pagingResult,
      };

      return of(new HttpResponse({
        status: 200,
        body: responseBody
      })).pipe(
        delay(record.delayInMsec ?? 500) // <-- simulate a real network call
      );
    }

    // If not the target endpoint, pass the request through
    return next.handle(req);
  }

  /**
   * Applies create/update/delete mutations to the in-memory mock dataset.
   */
  private applyMutation(record: LocalRecordSet, req: HttpRequest<unknown>): unknown {
    const body = req.body ?? {};
    const dataset = Array.isArray(record.data) ? record.data as Array<Record<string, unknown>> : [];

    if (req.method === 'DELETE') {
      const deleteId = this.resolveMutationId(record.id, req, dataset);
      const deleted = deleteId ? this.deleteRecord(dataset, deleteId) : false;
      return { id: deleteId, deleted };
    }

    // Handle DynamicForm draft envelopes
    if (body && typeof body === 'object') {
      const bodyObj = body as Record<string, unknown>;

      if (bodyObj['payload'] !== undefined) {
        let payload: unknown = bodyObj['payload'];
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch {
            payload = {};
          }
        }

        const payloadObj = typeof payload === 'object' && payload !== null ? payload as Record<string, unknown> : {};
        const entryId = this.resolveUpsertId(record.id, req, payloadObj, bodyObj, dataset, req.method);
        this.applyEndpointDefaults(record.id, req.method, dataset, payloadObj);
        this.upsertRecord(dataset, entryId, payloadObj, this.resolveInsertMode(record.id));
        return { ...payloadObj, id: entryId };
      }

      const entryId = this.resolveUpsertId(record.id, req, bodyObj, bodyObj, dataset, req.method);
      this.applyEndpointDefaults(record.id, req.method, dataset, bodyObj);
      this.upsertRecord(dataset, entryId, bodyObj, this.resolveInsertMode(record.id));
      return { ...bodyObj, id: entryId };
    }

    // If body isn't an object, just return raw body and leave data untouched
    return body;
  }

  /**
   * Resolves the target record id from URL path or request body.
   */
  private resolveMutationId(
    recordId: string,
    req: HttpRequest<unknown>,
    dataset: Array<Record<string, unknown>>
  ): string | undefined {
    const urlWithoutQuery = req.url.split('?')[0] ?? '';
    const pathSegments = urlWithoutQuery.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    const recordSegments = recordId.split('/').filter(Boolean);
    const recordTailSegment = recordSegments[recordSegments.length - 1]?.toLowerCase();

    if (lastSegment && !lastSegment.includes('{') && lastSegment.toLowerCase() !== recordTailSegment) {
      return decodeURIComponent(lastSegment);
    }

    if (req.body && typeof req.body === 'object') {
      const bodyObj = req.body as Record<string, unknown>;
      const bodyId = bodyObj['id'] ?? bodyObj['key'];
      if (bodyId !== undefined && bodyId !== null) {
        return String(bodyId);
      }
    }

    return dataset[0]?.['id'] ? String(dataset[0]['id']) : undefined;
  }

  /**
   * Resolves the id used for create/update upserts.
   * POST without an id generates a new record id so create requests append
   * instead of overwriting the first item in the dataset.
   */
  private resolveUpsertId(
    recordId: string,
    req: HttpRequest<unknown>,
    primaryBody: Record<string, unknown>,
    fallbackBody: Record<string, unknown>,
    dataset: Array<Record<string, unknown>>,
    method: string,
  ): string {
    const explicitId = primaryBody['id']
      ?? primaryBody['key']
      ?? fallbackBody['id']
      ?? fallbackBody['key'];

    if (explicitId !== undefined && explicitId !== null && String(explicitId).trim().length > 0) {
      return String(explicitId);
    }

    if (method === 'POST') {
      return this.generateMockRecordId(dataset);
    }

    const mutationId = this.resolveMutationId(recordId, req, dataset);
    if (mutationId) {
      return mutationId;
    }

    return dataset[0]?.['id'] ? String(dataset[0]['id']) : 'mock-item-001';
  }

  /**
   * Generates the next stable mock id for appended in-memory records.
   */
  private generateMockRecordId(dataset: Array<Record<string, unknown>>): string {
    const existingIds = dataset.map((row) => String(row['id'] ?? '')).filter((value) => value.trim().length > 0);
    const patternedIds = existingIds
      .map((id) => {
        const match = /^(.*?)(\d+)$/.exec(id);
        if (!match) {
          return undefined;
        }

        return {
          prefix: match[1] ?? '',
          numericPart: Number.parseInt(match[2] ?? '', 10),
          width: (match[2] ?? '').length,
        };
      })
      .filter((value): value is { prefix: string; numericPart: number; width: number } =>
        !!value && Number.isFinite(value.numericPart)
      );

    if (patternedIds.length) {
      const preferredPrefix = patternedIds[0].prefix;
      const matchingPrefixIds = patternedIds.filter((item) => item.prefix === preferredPrefix);
      const nextNumericPart = Math.max(...matchingPrefixIds.map((item) => item.numericPart)) + 1;
      const width = Math.max(...matchingPrefixIds.map((item) => item.width));
      return `${preferredPrefix}${String(nextNumericPart).padStart(width, '0')}`;
    }

    return `mock-item-${dataset.length + 1}`;
  }

  /**
   * Upserts a record into an in-memory store by `id`.
   */
  private upsertRecord(
    store: Array<Record<string, unknown>>,
    id: string,
    value: Record<string, unknown>,
    insertMode: 'prepend' | 'append' = 'prepend',
  ): void {
    if (!Array.isArray(store)) {
      return;
    }
    const index = store.findIndex((r) => r['id'] === id);
    if (index >= 0) {
      store[index] = { ...store[index], ...value, id };
    } else {
      const nextRecord = { id, ...value };
      if (insertMode === 'append') {
        store.push(nextRecord);
      } else {
        store.unshift(nextRecord);
      }
    }
  }

  /**
   * Applies endpoint-specific defaults before writing the record.
   */
  private applyEndpointDefaults(
    _recordId: string,
    _method: string,
    _dataset: Array<Record<string, unknown>>,
    _value: Record<string, unknown>,
  ): void {
    return;
  }

  /**
   * Chooses whether newly created mock rows are prepended or appended.
   */
  private resolveInsertMode(_recordId: string): 'prepend' | 'append' {
    return 'prepend';
  }

  /**
   * Deletes a record from an in-memory store by `id`.
   */
  private deleteRecord(store: Array<Record<string, unknown>>, id: string): boolean {
    if (!Array.isArray(store) || !id) {
      return false;
    }

    const index = store.findIndex((row) => String(row['id']) === id);
    if (index < 0) {
      return false;
    }

    store.splice(index, 1);
    return true;
  }

  /**
   * Determines whether the current URL should use OData parsing.
   */
  private isODataRequest(url: URL): boolean {
    return isLikelyODataRequest(url);
  }
}
